import SidePanel from '../SidePanel';
import React, { useState } from 'react';
import Questions, {
    getIcon,
    PaymentQuestionCategories,
    QuestionCategories,
    QuestionType,
    ThankYouQuestionCategories,
} from '../../lib/Questions';
import {
    CUSTOM_QUESTION_CATEGORY,
    InvoiceQuestionCategories,
} from '../../lib/Questions/Questions.constants';
import TStrings from '~/lib/Questions/Questions.i18n';
import { useFeatures } from '~/lib/Features';
import { useCustomQuestions } from '~/lib/CustomQuestions/CustomQuestionRegistry';
import {
    DEFAULT_SECTION_TYPES,
    sectionTypeOf,
} from '~/lib/CustomQuestions/CustomQuestionApi';
import { customQuestionIcon } from '~/lib/CustomQuestions/CustomQuestionIcons';
import usePermissions from '~/lib/usePermissions';
import FullScreenModal from '~/components/FullScreenModal';
import { PlusIcon } from '@heroicons/react/24/outline';
import CustomQuestionModalEditor from '~/components/customquestions/CustomQuestionModalEditor';

/**
 * Where a custom question is listed within a panel.
 *
 * A definition can name one of the categories this panel is showing, to sit
 *  alongside the questions it belongs with. Anything else - including nothing,
 *  a category this kind of section doesn't have, and anything left over from a
 *  renamed category - goes in the Custom Questions group, so a question can
 *  never be written and then be impossible to find.
 */
function customQuestionCategory(definition, categoriesToShow) {
    const named = (definition?.category || '').trim();

    return categoriesToShow.includes(named) &&
        named !== CUSTOM_QUESTION_CATEGORY
        ? named
        : CUSTOM_QUESTION_CATEGORY;
}

const NewPill = () => (
    <span className="ml-2 rounded-full bg-primary-700 px-2 py-0.5 text-[10px] leading-3 font-medium text-white">
        New
    </span>
);

const AddQuestionPanel = ({
    isOpen,
    onClose,
    onAddQuestion,
    section,
    isSaving,
    hasScheduling,
}) => {
    const [label, setLabel] = useState('');
    const [type, setType] = useState('');
    const [lastClickedKey, setLastClickedKey] = useState(null);
    // Writing a new custom question without leaving the form designer
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [modalQuestionId, setModalQuestionId] = useState(0);

    function openCreateModal() {
        setModalQuestionId(0);
        setShowCreateModal(true);
    }
    const { isEnabled } = useFeatures();
    const customQuestions = useCustomQuestions();
    const { isFullAccess } = usePermissions();

    function submit(ev) {
        ev.preventDefault();

        onAddQuestion({ label, type });
    }

    function addCustom(definition) {
        // Every custom question is the same question type - which definition it
        //  uses is a setting on the question
        onAddQuestion({
            type: QuestionType.Custom,
            label: definition.name,
            settings: { customQuestionId: definition.id },
        });
    }

    function add(type) {
        // Are we changing this from a non-scheduling form to scheduling?
        if (
            !hasScheduling &&
            Questions[type]?.category === TStrings.QUESTION_CATEGORY_SCHEDULING
        ) {
            if (
                !confirm(
                    'You are changing this to a scheduling form. Any Automations you customized will be removed and replaced with the default scheduling automations.\n\nAre you sure you want to add scheduling to this form?',
                )
            ) {
                // They don't want to!
                return false;
            }
        }
        onAddQuestion({ type });
    }

    let categoriesToShow = QuestionCategories;
    if (section?.settings?.thankyou) {
        categoriesToShow = ThankYouQuestionCategories;
    } else if (section?.settings?.payment) {
        categoriesToShow = PaymentQuestionCategories;
    } else if (section?.settings?.invoice) {
        categoriesToShow = InvoiceQuestionCategories;
    }

    // Custom questions can be written for any kind of section, so the group is
    //  offered everywhere - which of them appear in it is up to each one. It
    //  always comes first, so it doesn't move when the first one is saved
    categoriesToShow = [
        CUSTOM_QUESTION_CATEGORY,
        ...categoriesToShow.filter(c => c !== CUSTOM_QUESTION_CATEGORY),
    ];

    const sectionType = sectionTypeOf(section);

    // Only the questions set up for this kind of section, which is also
    //  enforced server side when one is added
    const availableCustomQuestions = customQuestions.filter(definition =>
        (definition.flags?.sections ?? DEFAULT_SECTION_TYPES).includes(
            sectionType,
        ),
    );

    const byCategory = [];
    for (const category of categoriesToShow) {
        byCategory.push({
            category,
            questions: Object.keys(Questions).filter(
                key =>
                    Questions[key].category === category &&
                    !Questions[key].hideFromAddPanel &&
                    (!Questions[key].feature ||
                        isEnabled(Questions[key].feature)),
            ),
            // A business's own questions are listed one by one rather than as
            //  the single type they all share
            customQuestions: availableCustomQuestions.filter(
                definition =>
                    customQuestionCategory(definition, categoriesToShow) ===
                    category,
            ),
        });
    }

    const newCustomQuestion = (
        <button
            type="button"
            className="ml-auto flex items-center text-sm font-normal link"
            onClick={openCreateModal}
        >
            <PlusIcon className="w-4 h-4 mr-0.5" />
            New
        </button>
    );

    return (
        <SidePanel
            isOpen={isOpen}
            onClose={onClose}
            title="Add Element"
            widthClass="w-80"
            paddingXClass="px-4"
        >
            {byCategory
                .filter(
                    cat =>
                        cat.questions.length > 0 ||
                        cat.customQuestions.length > 0 ||
                        // Where a full access user writes their first one
                        (cat.category === CUSTOM_QUESTION_CATEGORY &&
                            isFullAccess()),
                ) // not showing categories without questions
                .map((data, idx) => (
                    <div key={idx}>
                        <div
                            className={`font-semibold mb-1 ${idx > 0 ? 'mt-6' : ''} ml-2 flex items-center`}
                        >
                            {data.category}
                            {data.category === CUSTOM_QUESTION_CATEGORY && (
                                <>
                                    <NewPill />
                                    {isFullAccess() &&
                                        availableCustomQuestions.length > 0 &&
                                        newCustomQuestion}
                                </>
                            )}
                        </div>
                        {data.customQuestions.map(definition => {
                            const MyIcon = customQuestionIcon(definition.icon);
                            const key = `custom-${definition.id}`;
                            const isAdding = lastClickedKey === key && isSaving;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    title={definition.description || ''}
                                    className={`${isAdding ? 'text-gray-500' : ''} my-2 w-full flex items-center text-left rounded-md px-3 py-2 hover:bg-gray-300 `}
                                    onClick={() => {
                                        addCustom(definition);
                                        setLastClickedKey(key);
                                    }}
                                >
                                    <MyIcon className="w-6 h-6 p-1 rounded mr-2 text-primary-700 bg-primary-50" />
                                    {isAdding ? (
                                        <>Adding...</>
                                    ) : (
                                        definition.name
                                    )}
                                </button>
                            );
                        })}
                        {data.questions.map(key => {
                            const MyIcon = getIcon(Questions[key]);
                            const isAdding = lastClickedKey === key && isSaving;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    className={`${isAdding ? 'text-gray-500' : ''} my-2 w-full flex items-center text-left rounded-md px-3 py-2 hover:bg-gray-300 `}
                                    onClick={() => {
                                        add(key);
                                        setLastClickedKey(key);
                                    }}
                                >
                                    <MyIcon className="w-6 h-6 p-1 rounded mr-2 text-primary-700 bg-primary-50" />
                                    {isAdding && <>Adding...</>}
                                    {!isAdding && Questions[key].name}
                                </button>
                            );
                        })}
                        {data.category === CUSTOM_QUESTION_CATEGORY &&
                            isFullAccess() &&
                            !availableCustomQuestions.length && (
                                <div className="ml-2">
                                    <button
                                        type="button"
                                        className="text-sm link"
                                        onClick={openCreateModal}
                                    >
                                        Create custom question
                                    </button>
                                </div>
                            )}
                    </div>
                ))}

            <FullScreenModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                // Backdrop matches the panel so no frame shows while the panel
                //  scales, and both match the page the editor normally sits on
                backgroundClassName="!bg-gray-100 !bg-opacity-100"
                panelClassName="!bg-gray-100 !overflow-auto"
            >
                {/* About the width the editor page gets beside the nav on a
                    laptop screen */}
                <div className="max-w-6xl mx-auto">
                    <CustomQuestionModalEditor
                        customQuestionId={modalQuestionId}
                        // The assistant saves as it goes, so stay open on the
                        //  question it created
                        onCreated={setModalQuestionId}
                        // Saving refreshes this form, which brings the question
                        //  into the list above
                        onSaved={() => setShowCreateModal(false)}
                        onDeleted={() => setShowCreateModal(false)}
                    />
                </div>
            </FullScreenModal>
        </SidePanel>
    );
};

export default AddQuestionPanel;
